import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiTrendingDown, FiMinus, FiRefreshCw, FiSave, FiUsers, FiCheck } = FiIcons;

const Results = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [savingStatus, setSavingStatus] = useState('idle'); // idle, saving, success, error
  const [userTeams, setUserTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [showTeamSelector, setShowTeamSelector] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);

  useEffect(() => {
    const storedResults = localStorage.getItem('assessmentResults');
    if (storedResults) {
      const data = JSON.parse(storedResults);
      setAssessmentData(data);
      calculateResults(data);
      loadUserTeams();
    } else {
      navigate('/dashboard');
    }
  }, [navigate]);

  const loadUserTeams = () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const allTeams = JSON.parse(localStorage.getItem('teams') || '[]');
      const userTeams = allTeams.filter(team => team.ownerId === userData.id);
      setUserTeams(userTeams);
      
      // Auto-select if there's already a team associated with this assessment
      if (assessmentData?.teamId) {
        setSelectedTeam(assessmentData.teamId.toString());
      } else if (userTeams.length === 1) {
        setSelectedTeam(userTeams[0].id.toString());
      }
    } catch (error) {
      console.error('Error loading user teams:', error);
    }
  };

  const calculateResults = (responses) => {
    const sections = [
      'Psychological Safety',
      'Dependability', 
      'Structure & Clarity',
      'Meaning',
      'Impact'
    ];
    const sectionQuestionCounts = [7, 5, 5, 5, 5];
    const reverseScoreQuestions = ['0-0', '0-2', '0-4', '1-1']; // Questions that need reverse scoring

    const sectionScores = sections.map((section, sectionIndex) => {
      let totalScore = 0;
      let questionCount = sectionQuestionCounts[sectionIndex];
      
      for (let q = 0; q < questionCount; q++) {
        const key = `${sectionIndex}-${q}`;
        let score = responses.responses[key] || 3;
        
        // Reverse score certain questions
        if (reverseScoreQuestions.includes(key)) {
          score = 6 - score;
        }
        
        totalScore += score;
      }
      
      const average = totalScore / questionCount;
      const percentage = ((average - 1) / 4) * 100;
      
      return {
        name: section,
        score: average,
        percentage: Math.round(percentage),
        level: getScoreLevel(average)
      };
    });

    const overallScore = sectionScores.reduce((sum, section) => sum + section.score, 0) / sections.length;
    const overallPercentage = Math.round(((overallScore - 1) / 4) * 100);

    setResults({
      sections: sectionScores,
      overall: {
        score: overallScore,
        percentage: overallPercentage,
        level: getScoreLevel(overallScore)
      }
    });
  };

  const saveResultsToTeam = async (teamId) => {
    if (!teamId || !results) {
      console.error('No team selected or no results to save');
      return;
    }

    setSavingStatus('saving');

    try {
      // Get all teams from localStorage
      const allTeams = JSON.parse(localStorage.getItem('teams') || '[]');
      
      // Find the specific team
      const teamIndex = allTeams.findIndex(t => t.id === parseInt(teamId));
      
      if (teamIndex === -1) {
        console.error('Team not found:', teamId);
        setSavingStatus('error');
        return;
      }

      const previousScore = allTeams[teamIndex].overallScore || 0;

      // Update team with new assessment results
      const updatedTeam = {
        ...allTeams[teamIndex],
        lastAssessment: new Date().toISOString().split('T')[0],
        overallScore: results.overall.percentage,
        status: results.overall.level.level,
        trend: calculateTrend(previousScore, results.overall.percentage),
        sectionScores: results.sections,
        assessmentHistory: [
          ...(allTeams[teamIndex].assessmentHistory || []),
          {
            date: new Date().toISOString(),
            overallScore: results.overall.percentage,
            sectionScores: results.sections
          }
        ]
      };

      // Update the team in the array
      allTeams[teamIndex] = updatedTeam;
      
      // Save back to localStorage
      localStorage.setItem('teams', JSON.stringify(allTeams));
      
      // Update assessment data with team info
      const updatedAssessmentData = {
        ...assessmentData,
        teamId: parseInt(teamId),
        teamName: updatedTeam.name,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem('assessmentResults', JSON.stringify(updatedAssessmentData));
      
      setSavingStatus('success');
      setHasSaved(true);
      setShowTeamSelector(false);
      
      console.log('Assessment results saved to team successfully');
      
      // Clear success status after 3 seconds
      setTimeout(() => setSavingStatus('idle'), 3000);
      
    } catch (error) {
      console.error('Error saving results to team:', error);
      setSavingStatus('error');
      
      // Clear error status after 3 seconds
      setTimeout(() => setSavingStatus('idle'), 3000);
    }
  };

  const calculateTrend = (previousScore, currentScore) => {
    if (!previousScore || previousScore === 0) return 'stable';
    if (currentScore > previousScore + 5) return 'up';
    if (currentScore < previousScore - 5) return 'down';
    return 'stable';
  };

  const getScoreLevel = (score) => {
    if (score >= 4.5) return { level: 'Excellent', color: 'green', icon: FiTrendingUp };
    if (score >= 3.5) return { level: 'Good', color: 'blue', icon: FiTrendingUp };
    if (score >= 2.5) return { level: 'Fair', color: 'yellow', icon: FiMinus };
    return { level: 'Needs Improvement', color: 'red', icon: FiTrendingDown };
  };

  const getRecommendations = (sectionName, score) => {
    const recommendations = {
      'Psychological Safety': {
        low: [
          'Establish team norms that encourage open dialogue',
          'Practice active listening during meetings',
          'Share failures as learning opportunities',
          'Encourage questions and curiosity'
        ],
        high: [
          'Continue fostering open communication',
          'Share your practices with other teams',
          'Mentor new team members on psychological safety'
        ]
      },
      'Dependability': {
        low: [
          'Implement clear task tracking systems',
          'Set realistic deadlines and expectations',
          'Establish regular check-ins and status updates',
          'Create accountability partnerships'
        ],
        high: [
          'Maintain your strong commitment culture',
          'Document your processes for other teams',
          'Continue celebrating reliability'
        ]
      },
      'Structure & Clarity': {
        low: [
          'Define clear roles and responsibilities',
          'Document team processes and procedures',
          'Establish regular goal-setting sessions',
          'Create visual project dashboards'
        ],
        high: [
          'Keep documentation up to date',
          'Share your clarity practices',
          'Continue regular goal alignment'
        ]
      },
      'Meaning': {
        low: [
          'Connect daily tasks to larger purpose',
          'Share impact stories and user feedback',
          'Discuss personal career goals',
          'Celebrate meaningful achievements'
        ],
        high: [
          'Continue connecting work to purpose',
          'Help others find meaning in their work',
          'Share success stories regularly'
        ]
      },
      'Impact': {
        low: [
          'Make team contributions more visible',
          'Track and share success metrics',
          'Connect with end users/customers',
          'Celebrate team achievements publicly'
        ],
        high: [
          'Continue showcasing your impact',
          'Mentor other teams on visibility',
          'Keep measuring and sharing results'
        ]
      }
    };

    return recommendations[sectionName]?.[score >= 3.5 ? 'high' : 'low'] || [];
  };

  if (!results) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <p className="text-gray-600">Loading your results...</p>
          <Link to="/assessment" className="text-blue-600 hover:underline mt-4 inline-block">
            Take the assessment first
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Save Status Notification */}
      {savingStatus === 'saving' && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            <p className="text-sm text-blue-800">Saving assessment results to your team...</p>
          </div>
        </div>
      )}
      
      {savingStatus === 'success' && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-600 mr-2" />
            <p className="text-sm text-green-800">Assessment results saved successfully!</p>
          </div>
        </div>
      )}
      
      {savingStatus === 'error' && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <SafeIcon icon={FiTrendingDown} className="h-4 w-4 text-red-600 mr-2" />
            <p className="text-sm text-red-800">Error saving results. Please try again.</p>
          </div>
        </div>
      )}

      {/* Overall Score */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-6">Your Team Assessment Results</h1>
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md mx-auto">
          <div className="flex items-center justify-center mb-4">
            <SafeIcon icon={results.overall.level.icon} className={`h-16 w-16 text-${results.overall.level.color}-500`} />
          </div>
          <div className="text-6xl font-bold text-gray-900 mb-2">
            {results.overall.percentage}%
          </div>
          <div className={`text-xl font-semibold text-${results.overall.level.color}-600 mb-2`}>
            {results.overall.level.level}
          </div>
          <p className="text-gray-600">Overall Team Health Score</p>
          {assessmentData?.teamName && (
            <p className="text-sm text-gray-500 mt-2">Team: {assessmentData.teamName}</p>
          )}
        </div>
      </div>

      {/* Save to Team Section */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Save Assessment Results</h2>
        
        {!hasSaved ? (
          <div className="space-y-4">
            <p className="text-gray-600">
              Save these assessment results to one of your teams to track progress over time and view them on your dashboard.
            </p>
            
            {userTeams.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiUsers} className="h-5 w-5 text-yellow-600" />
                  <p className="text-sm text-yellow-800">
                    You don't have any teams yet. 
                    <Link to="/dashboard" className="text-yellow-600 underline hover:text-yellow-700 ml-1">
                      Create a team first
                    </Link>
                    {' '}to save these results.
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <label htmlFor="team-select" className="block text-sm font-medium text-gray-700 mb-2">
                  Select Team
                </label>
                <div className="flex space-x-3">
                  <select
                    id="team-select"
                    value={selectedTeam}
                    onChange={(e) => setSelectedTeam(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Choose a team...</option>
                    {userTeams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name} {team.overallScore > 0 && `(${team.overallScore}% previous score)`}
                      </option>
                    ))}
                  </select>
                  
                  <button
                    onClick={() => saveResultsToTeam(selectedTeam)}
                    disabled={!selectedTeam || savingStatus === 'saving'}
                    className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <SafeIcon icon={FiSave} className="h-4 w-4" />
                    <span>{savingStatus === 'saving' ? 'Saving...' : 'Save to Team'}</span>
                  </button>
                </div>
                
                {selectedTeam && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">
                      <SafeIcon icon={FiUsers} className="h-4 w-4 inline mr-1" />
                      {userTeams.find(t => t.id === parseInt(selectedTeam))?.members?.length || 0} team members
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-800">Results saved successfully!</p>
                <p className="text-sm text-green-700">
                  These results have been saved to your team and are now visible on your dashboard.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Section Scores */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        {results.sections.map((section, index) => (
          <div key={index} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{section.name}</h3>
              <SafeIcon icon={section.level.icon} className={`h-6 w-6 text-${section.level.color}-500`} />
            </div>
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>{section.level.level}</span>
                <span>{section.percentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`bg-${section.level.color}-500 h-2 rounded-full`} 
                  style={{ width: `${section.percentage}%` }}
                />
              </div>
            </div>
            <p className="text-sm text-gray-600">
              Score: {section.score.toFixed(1)}/5.0
            </p>
          </div>
        ))}
      </div>

      {/* Detailed Recommendations */}
      <div className="space-y-8 mb-12">
        <h2 className="text-3xl font-bold text-gray-900 text-center">Personalized Recommendations</h2>
        {results.sections.map((section, index) => (
          <div key={index} className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{section.name}</h3>
              <div className={`px-4 py-2 rounded-full bg-${section.level.color}-100 text-${section.level.color}-800 font-semibold`}>
                {section.level.level}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Current Status</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-3xl font-bold text-gray-900 mb-2">{section.percentage}%</div>
                  <p className="text-gray-600">
                    Your team scored {section.score.toFixed(1)} out of 5.0 in this area.
                  </p>
                </div>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Action Items</h4>
                <ul className="space-y-2">
                  {getRecommendations(section.name, section.score).map((rec, recIndex) => (
                    <li key={recIndex} className="flex items-start space-x-2">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0" />
                      <span className="text-gray-700">{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Clean Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link 
          to="/dashboard" 
          className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
        >
          <span>View on Dashboard</span>
        </Link>
        <Link 
          to="/assessment" 
          className="flex items-center justify-center space-x-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
        >
          <SafeIcon icon={FiRefreshCw} className="h-5 w-5" />
          <span>Retake Assessment</span>
        </Link>
      </div>
    </div>
  );
};

export default Results;